import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { UserPlusIcon, XMarkIcon, UserIcon } from '@heroicons/react/24/outline';
import api from '../../services/api';
import { Button } from '../common/Button';
import Modal from '../common/Modal';
import { useNotification } from '../../contexts/NotificationContext';

interface User {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  position?: string;
}

interface Assignment {
  id: number;
  assignedUser: User;
  assignedBy: User;
  created_at: string;
}

interface InvoiceAssignmentsProps {
  invoiceId: number;
}

export const InvoiceAssignments: React.FC<InvoiceAssignmentsProps> = ({ invoiceId }) => {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const { showNotification } = useNotification();
  const queryClient = useQueryClient();

  // Fetch current assignments
  const { data: assignments, isLoading: assignmentsLoading } = useQuery({
    queryKey: ['invoiceAssignments', invoiceId],
    queryFn: async () => {
      const response = await api.get(`/invoices/${invoiceId}/assignments`);
      return response.data.assignments as Assignment[];
    },
  });

  // Fetch available users for assignment
  const { data: availableUsers, isLoading: usersLoading } = useQuery({
    queryKey: ['availableUsersForAssignment', invoiceId],
    queryFn: async () => {
      const response = await api.get(`/invoices/${invoiceId}/available-users`);
      return response.data.users as User[];
    },
    enabled: isAddModalOpen,
  });

  // Add assignment mutation
  const addAssignmentMutation = useMutation({
    mutationFn: async (userId: number) => {
      const response = await api.post(`/invoices/${invoiceId}/assignments`, {
        user_id: userId,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoiceAssignments', invoiceId] });
      queryClient.invalidateQueries({ queryKey: ['availableUsersForAssignment', invoiceId] });
      setIsAddModalOpen(false);
      setSelectedUserId(null);
      showNotification({
        type: 'success',
        title: 'Opiekun dodany',
        message: 'Opiekun został pomyślnie przypisany do faktury',
      });
    },
    onError: (error: any) => {
      showNotification({
        type: 'error',
        title: 'Błąd',
        message: error.response?.data?.error || 'Nie udało się dodać opiekuna',
      });
    },
  });

  // Remove assignment mutation
  const removeAssignmentMutation = useMutation({
    mutationFn: async (userId: number) => {
      await api.delete(`/invoices/${invoiceId}/assignments/${userId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoiceAssignments', invoiceId] });
      queryClient.invalidateQueries({ queryKey: ['availableUsersForAssignment', invoiceId] });
      showNotification({
        type: 'success',
        title: 'Opiekun usunięty',
        message: 'Opiekun został usunięty z faktury',
      });
    },
    onError: (error: any) => {
      showNotification({
        type: 'error',
        title: 'Błąd',
        message: error.response?.data?.error || 'Nie udało się usunąć opiekuna',
      });
    },
  });

  const handleAddAssignment = () => {
    if (selectedUserId) {
      addAssignmentMutation.mutate(selectedUserId);
    }
  };

  const handleRemoveAssignment = (userId: number) => {
    if (window.confirm('Czy na pewno chcesz usunąć tego opiekuna z faktury?')) {
      removeAssignmentMutation.mutate(userId);
    }
  };

  if (assignmentsLoading) {
    return <div className="animate-pulse bg-gray-100 h-32 rounded"></div>;
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900 flex items-center">
          <UserIcon className="h-5 w-5 mr-2" />
          Opiekunowie faktury
        </h3>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setIsAddModalOpen(true)}
          className="flex items-center"
        >
          <UserPlusIcon className="h-4 w-4 mr-1" />
          Dodaj opiekuna
        </Button>
      </div>

      {assignments && assignments.length > 0 ? (
        <div className="space-y-2">
          {assignments.map((assignment) => (
            <div
              key={assignment.id}
              className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
            >
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0">
                  <div className="h-8 w-8 bg-primary-500 rounded-full flex items-center justify-center">
                    <span className="text-sm font-medium text-white">
                      {assignment.assignedUser.first_name.charAt(0)}
                      {assignment.assignedUser.last_name.charAt(0)}
                    </span>
                  </div>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    {assignment.assignedUser.first_name} {assignment.assignedUser.last_name}
                  </p>
                  <p className="text-sm text-gray-500">{assignment.assignedUser.email}</p>
                  <p className="text-xs text-gray-400">
                    Przypisany przez: {assignment.assignedBy.first_name} {assignment.assignedBy.last_name}
                  </p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleRemoveAssignment(assignment.assignedUser.id)}
                disabled={removeAssignmentMutation.isPending}
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                <XMarkIcon className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-6 text-gray-500">
          <UserIcon className="h-12 w-12 mx-auto mb-2 text-gray-300" />
          <p className="text-sm">Brak przypisanych opiekunów</p>
          <p className="text-xs text-gray-400">Kliknij "Dodaj opiekuna" aby przypisać pracownika</p>
        </div>
      )}

      {/* Add Assignment Modal */}
      <Modal
        isOpen={isAddModalOpen}
        onClose={() => {
          setIsAddModalOpen(false);
          setSelectedUserId(null);
        }}
        title="Dodaj opiekuna faktury"
      >
        <div className="p-6">
          <p className="text-sm text-gray-600 mb-4">
            Wybierz pracownika, który będzie opiekunem tej faktury. Opiekunowie otrzymują powiadomienia o zmianach statusu faktury.
          </p>

          {usersLoading ? (
            <div className="animate-pulse space-y-2">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-12 bg-gray-100 rounded"></div>
              ))}
            </div>
          ) : availableUsers && availableUsers.length > 0 ? (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {availableUsers.map((user) => (
                <label
                  key={user.id}
                  className="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-gray-50"
                >
                  <input
                    type="radio"
                    name="selectedUser"
                    value={user.id}
                    checked={selectedUserId === user.id}
                    onChange={() => setSelectedUserId(user.id)}
                    className="mr-3"
                  />
                  <div className="flex items-center space-x-3">
                    <div className="h-8 w-8 bg-primary-500 rounded-full flex items-center justify-center">
                      <span className="text-sm font-medium text-white">
                        {user.first_name.charAt(0)}{user.last_name.charAt(0)}
                      </span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {user.first_name} {user.last_name}
                      </p>
                      <p className="text-sm text-gray-500">{user.email}</p>
                      {user.position && (
                        <p className="text-xs text-gray-400">{user.position}</p>
                      )}
                    </div>
                  </div>
                </label>
              ))}
            </div>
          ) : (
            <div className="text-center py-6 text-gray-500">
              <UserIcon className="h-12 w-12 mx-auto mb-2 text-gray-300" />
              <p className="text-sm">Brak dostępnych pracowników do przypisania</p>
            </div>
          )}

          <div className="flex justify-end space-x-3 mt-6">
            <Button
              variant="outline"
              onClick={() => {
                setIsAddModalOpen(false);
                setSelectedUserId(null);
              }}
            >
              Anuluj
            </Button>
            <Button
              variant="primary"
              onClick={handleAddAssignment}
              disabled={!selectedUserId || addAssignmentMutation.isPending}
              isLoading={addAssignmentMutation.isPending}
            >
              Dodaj opiekuna
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};